import type {
    FrontendErrorFrame,
    FrontendErrorsCategory,
    FrontendErrorsDistribution,
    FrontendErrorsSourceReason,
    FrontendErrorsSourceStatus,
} from "@/features/observability/types/frontend-errors.types";

// Mirrors backend `modules/observability/platform-errors/domain/platform-errors.types.ts` (vb-metrics#46).

export type PlatformEnvironment = "prod" | "pre" | "stage";

// The backend's single severity vocabulary (filter enum and series axis, in this order): PLATFORM_SEVERITIES below.
export type PlatformSeverity = "EMERGENCY" | "ALERT" | "CRITICAL" | "ERROR" | "INFO";

export type PlatformHealthStatus = "ok" | "degraded" | "unknown" | "disabled";

export type PlatformCollector = {
    status: FrontendErrorsSourceStatus;
    checkedAt: number | null;
    platformStatus: PlatformHealthStatus | null;
    reasons: string[];
    backlog: number | null;
    oldestAgeSec: number | null;
};

export type PlatformErrorsSourceState = {
    sourceStatus: FrontendErrorsSourceStatus;
    reason: FrontendErrorsSourceReason | null;
    collector: PlatformCollector;
    countsAreLowerBound: boolean;
    stale: boolean;
    environment: string;
    asOf: number;
};

export type PlatformErrorsSummary = PlatformErrorsSourceState & {
    from: number;
    to: number;
    total: number | null;
    byService: FrontendErrorsDistribution | null;
    bySeverity: FrontendErrorsDistribution | null;
    byResourceType: FrontendErrorsDistribution | null;
    distinctFingerprints: number | null;
    freshness: { lastReceivedAt: number | null; lastIndexedAt: number | null } | null;
    coverage: { rejected: number | null; byReason: FrontendErrorsDistribution } | null;
};

export type PlatformErrorsSeriesBucket = {
    start: number;
    end: number;
    madridLabel: string;
    count: number | null;
    bySeverity: { severity: PlatformSeverity; count: number | null }[];
};

export type PlatformErrorsSeries = PlatformErrorsSourceState & {
    from: number;
    to: number;
    bucketMs: number;
    buckets: PlatformErrorsSeriesBucket[] | null;
};

export type PlatformErrorGroup = {
    service: string | null;
    fingerprint: string | null;
    count: number;
    firstSeenAt: number | null;
    lastSeenAt: number | null;
    app: string | null;
    errorCode: string | null;
    category: string | null;
    severity: string | null;
    resourceType: string | null;
    errorClass: string | null;
    httpStatus: number | null;
    logName: string | null;
    lastMessage: string | null;
};

export type PlatformErrorsGroups = PlatformErrorsSourceState & {
    total: number | null;
    groups: PlatformErrorGroup[] | null;
};

export type PlatformErrorEvent = {
    receiptId: string;
    effectiveOccurredAt: number;
    receivedAt: number | null;
    app: string | null;
    service: string | null;
    severity: string | null;
    resourceType: string | null;
    logName: string | null;
    category: string | null;
    errorCode: string | null;
    errorClass: string | null;
    fingerprint: string | null;
    message: string | null;
    httpStatus: number | null;
    frames: FrontendErrorFrame[] | null;
};

export type PlatformErrorsEvents = PlatformErrorsSourceState & {
    events: PlatformErrorEvent[] | null;
    nextCursor: string | null;
};

export type PlatformErrorsQueryParams = {
    environment: PlatformEnvironment | null;
    from: number;
    to: number;
    service: string | null;
    severity: PlatformSeverity | null;
    resourceType: string | null;
    errorCode: string | null;
    category: FrontendErrorsCategory | null;
};

export type PlatformErrorsPopulationParams = PlatformErrorsQueryParams & { asOf: number };

export type PlatformErrorsEventsParams = PlatformErrorsPopulationParams & { limit: number; fingerprint: string | null };

export type PlatformErrorsEventsPageParams = PlatformErrorsEventsParams & { cursor: string | null };

// The writer's literal for a source without a registered app (cloud-functions appLogsV2, C5).
export const UNREGISTERED_APP = "unregistered";

export const PLATFORM_ENVIRONMENTS: PlatformEnvironment[] = ["prod", "pre", "stage"];

export const PLATFORM_SEVERITIES: PlatformSeverity[] = ["EMERGENCY", "ALERT", "CRITICAL", "ERROR", "INFO"];

type Row = Record<string, unknown>;

const isRow = (value: unknown): value is Row => typeof value === "object" && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const isNullableNumber = (value: unknown): value is number | null => value === null || isNumber(value);

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === "string";

const isString = (value: unknown): value is string => typeof value === "string";

const isArrayOf = <T,>(value: unknown, guard: (item: unknown) => item is T): value is T[] =>
    Array.isArray(value) && value.every((item) => guard(item));

const isOneOf = <T extends string>(values: readonly T[]) => (value: unknown): value is T =>
    typeof value === "string" && values.some((candidate) => candidate === value);

const isSourceStatus = isOneOf<FrontendErrorsSourceStatus>(["ok", "degraded", "unknown"]);
const isSourceReason = isOneOf<FrontendErrorsSourceReason>(["index_missing", "timeout", "shards_failed", "groups_truncated", "transport"]);
const isHealthStatus = isOneOf<PlatformHealthStatus>(["ok", "degraded", "unknown", "disabled"]);

const isEnvironment = isOneOf(PLATFORM_ENVIRONMENTS);
const isSeverity = isOneOf(PLATFORM_SEVERITIES);

const isCollector = (value: unknown): value is PlatformCollector =>
    isRow(value)
    && isSourceStatus(value.status)
    && isNullableNumber(value.checkedAt)
    && (value.platformStatus === null || isHealthStatus(value.platformStatus))
    && isArrayOf(value.reasons, isString)
    && isNullableNumber(value.backlog)
    && isNullableNumber(value.oldestAgeSec);

const isSourceState = (value: Row): boolean =>
    isSourceStatus(value.sourceStatus)
    && (value.reason === null || isSourceReason(value.reason))
    && isCollector(value.collector)
    && typeof value.countsAreLowerBound === "boolean"
    && typeof value.stale === "boolean"
    && typeof value.environment === "string"
    && isNumber(value.asOf);

const isDistributionBucket = (value: unknown): value is { key: string; count: number } =>
    isRow(value) && typeof value.key === "string" && isNumber(value.count);

const isDistribution = (value: unknown): value is FrontendErrorsDistribution =>
    isRow(value) && isArrayOf(value.buckets, isDistributionBucket) && isNumber(value.other);

const isNullableDistribution = (value: unknown): boolean => value === null || isDistribution(value);

const isFreshness = (value: unknown): boolean =>
    isRow(value) && isNullableNumber(value.lastReceivedAt) && isNullableNumber(value.lastIndexedAt);

const isCoverage = (value: unknown): boolean =>
    isRow(value) && isNullableNumber(value.rejected) && isDistribution(value.byReason);

const isSummary = (value: unknown): value is PlatformErrorsSummary =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.from)
    && isNumber(value.to)
    && isNullableNumber(value.total)
    && isNullableDistribution(value.byService)
    && isNullableDistribution(value.bySeverity)
    && isNullableDistribution(value.byResourceType)
    && isNullableNumber(value.distinctFingerprints)
    && (value.freshness === null || isFreshness(value.freshness))
    && (value.coverage === null || isCoverage(value.coverage));

const isSeverityCount = (value: unknown): value is { severity: PlatformSeverity; count: number | null } =>
    isRow(value) && isSeverity(value.severity) && isNullableNumber(value.count);

const isSeriesBucket = (value: unknown): value is PlatformErrorsSeriesBucket =>
    isRow(value)
    && isNumber(value.start)
    && isNumber(value.end)
    && typeof value.madridLabel === "string"
    && isNullableNumber(value.count)
    && isArrayOf(value.bySeverity, isSeverityCount);

const isSeries = (value: unknown): value is PlatformErrorsSeries =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.from)
    && isNumber(value.to)
    && isNumber(value.bucketMs)
    && value.bucketMs > 0
    && (value.buckets === null || isArrayOf(value.buckets, isSeriesBucket));

const isGroup = (value: unknown): value is PlatformErrorGroup =>
    isRow(value)
    && isNullableString(value.service)
    && isNullableString(value.fingerprint)
    && isNumber(value.count)
    && isNullableNumber(value.firstSeenAt)
    && isNullableNumber(value.lastSeenAt)
    && isNullableString(value.app)
    && isNullableString(value.errorCode)
    && isNullableString(value.category)
    && isNullableString(value.severity)
    && isNullableString(value.resourceType)
    && isNullableString(value.errorClass)
    && isNullableNumber(value.httpStatus)
    && isNullableString(value.logName)
    && isNullableString(value.lastMessage);

const isGroups = (value: unknown): value is PlatformErrorsGroups =>
    isRow(value)
    && isSourceState(value)
    && isNullableNumber(value.total)
    && (value.groups === null || isArrayOf(value.groups, isGroup));

const isFrame = (value: unknown): value is FrontendErrorFrame =>
    isRow(value)
    && isNullableString(value.functionName)
    && isNullableString(value.asset)
    && isNullableNumber(value.line)
    && isNullableNumber(value.column);

const isEvent = (value: unknown): value is PlatformErrorEvent =>
    isRow(value)
    && typeof value.receiptId === "string"
    && isNumber(value.effectiveOccurredAt)
    && isNullableNumber(value.receivedAt)
    && isNullableString(value.app)
    && isNullableString(value.service)
    && isNullableString(value.severity)
    && isNullableString(value.resourceType)
    && isNullableString(value.logName)
    && isNullableString(value.category)
    && isNullableString(value.errorCode)
    && isNullableString(value.errorClass)
    && isNullableString(value.fingerprint)
    && isNullableString(value.message)
    && isNullableNumber(value.httpStatus)
    && (value.frames === null || isArrayOf(value.frames, isFrame));

const isEvents = (value: unknown): value is PlatformErrorsEvents =>
    isRow(value)
    && isSourceState(value)
    && isNullableString(value.nextCursor)
    && (value.events === null || isArrayOf(value.events, isEvent));

export const platformErrorsContract = {
    isEnvironment,
    isSeverity,
    isSummary,
    isSeries,
    isGroups,
    isEvents,
};
